import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import { CalendarCheck2, TrendingUp, Users, DoorOpen, AlertTriangle, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { CHANNEL_COLORS } from "@/lib/channels";

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);

const money2 = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v || 0);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [overdue, setOverdue] = useState(null);
  const navigate = useNavigate();
  const { company } = useCompany();

  useEffect(() => {
    setStats(null);
    api.get("/stats/summary").then((r) => setStats(r.data));
    api.get("/stats/upcoming", { params: { limit: 8 } }).then((r) => setUpcoming(r.data));
    api.get("/stats/overdue").then((r) => setOverdue(r.data));
  }, [company]);

  if (!stats) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const kpis = [
    { label: "Total Bookings", value: stats.total_bookings, icon: CalendarCheck2, testid: "kpi-total" },
    { label: "Upcoming Stays", value: stats.upcoming_count, icon: DoorOpen, testid: "kpi-upcoming" },
    { label: "Currently Checked-in", value: stats.current_count, icon: Users, testid: "kpi-current" },
    { label: "Total Host Payout", value: money(stats.total_revenue), icon: TrendingUp, testid: "kpi-revenue" },
  ];

  const channelData = (stats.channels || []).filter((c) => c.count > 0);

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-10 max-w-[1600px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Booking operations at a glance — {new Date().toLocaleDateString("en-GB", { dateStyle: "long" })}
          </p>
        </div>
      </div>

      {/* Overdue alert */}
      {overdue && overdue.count > 0 && (
        <div
          className="bg-rose-50 border border-rose-300 rounded-sm p-5 flex items-start gap-4"
          data-testid="overdue-alert"
        >
          <div className="w-10 h-10 bg-rose-600 rounded-sm flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow !text-rose-800">Unpaid & overdue</div>
            <div className="font-heading text-2xl font-semibold tracking-tight text-rose-900 mt-1">
              {overdue.count} pending payout{overdue.count === 1 ? "" : "s"} · {money2(overdue.total_owed)} owed
            </div>
            <p className="text-sm text-rose-800 mt-1">
              Guests have checked out (or expected payout date has passed) but payment status is still Pending.
            </p>
            {overdue.bookings.length > 0 && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {overdue.bookings.slice(0, 6).map((b) => (
                  <div
                    key={b.id}
                    onClick={() => navigate(`/bookings/${b.id}`)}
                    className="bg-white border border-rose-200 rounded-sm p-2.5 cursor-pointer hover:border-rose-400 transition-colors"
                    data-testid={`overdue-row-${b.id}`}
                  >
                    <div className="text-xs font-semibold text-slate-900 truncate">{b.property_booked || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{b.booked_by || "Guest"} · out {b.check_out || "—"}</div>
                    <div className="text-xs font-semibold text-rose-700 mt-1">{money2(b.host_payout)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => navigate("/payments")}
            className="text-xs font-semibold text-rose-800 hover:text-rose-900 label-eyebrow flex items-center gap-1 shrink-0"
            data-testid="overdue-view-all"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div
            key={k.label}
            data-testid={k.testid}
            className="bg-white border border-slate-200 rounded-sm p-6 hover:border-blue-900 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{k.label}</span>
              <k.icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="font-heading text-3xl font-semibold tracking-tight mt-4 text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-eyebrow">Revenue</div>
              <h3 className="font-heading text-xl font-medium mt-1 text-slate-900">Host Payout by Month</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenue_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${v}`} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", background: "#fff" }} />
                <Bar dataKey="revenue" fill="#1E3A8A" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="label-eyebrow">Channel Mix</div>
          <h3 className="font-heading text-xl font-medium mt-1 mb-4 text-slate-900">Bookings by Source</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={channelData} dataKey="count" nameKey="channel" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {channelData.map((c, i) => (
                    <Cell key={i} fill={CHANNEL_COLORS[c.channel] || "#94A3B8"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 gap-1.5 mt-4 text-xs">
            {channelData.map((c) => (
              <div key={c.channel} className="flex items-center justify-between">
                <div className="flex items-center gap-2 truncate">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: CHANNEL_COLORS[c.channel] || "#94A3B8" }} />
                  <span className="truncate text-slate-700">{c.channel}</span>
                </div>
                <span className="tabular-nums font-semibold text-slate-900">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Next 8</div>
            <h3 className="font-heading text-xl font-medium mt-1 text-slate-900">Upcoming Check-ins</h3>
          </div>
          <button
            onClick={() => navigate("/bookings")}
            className="text-xs font-semibold text-blue-900 hover:underline label-eyebrow"
            data-testid="view-all-bookings-btn"
          >
            View all →
          </button>
        </div>
        {upcoming.length === 0 ? (
          <div className="p-8 text-sm text-slate-500 text-center">No upcoming bookings.</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {upcoming.map((b) => (
              <div
                key={b.id}
                onClick={() => navigate(`/bookings/${b.id}`)}
                className="p-4 px-6 grid grid-cols-1 md:grid-cols-6 gap-3 hover:bg-blue-50/50 cursor-pointer transition-colors items-center"
                data-testid={`upcoming-row-${b.id}`}
              >
                <div>
                  <div className="text-xs text-slate-500 label-eyebrow">Property</div>
                  <div className="text-sm font-medium">{b.property_booked || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 label-eyebrow">Guest</div>
                  <div className="text-sm">{b.booked_by || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 label-eyebrow">Check-in</div>
                  <div className="text-sm font-medium">{b.check_in || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 label-eyebrow">Nights</div>
                  <div className="text-sm">{b.nights || 0}</div>
                </div>
                <div className="text-right md:text-left">
                  <div className="text-xs text-slate-500 label-eyebrow">Payout</div>
                  <div className="text-sm font-semibold font-heading text-blue-900">{money(b.host_payout)}</div>
                </div>
                <div className="flex justify-start md:justify-end">
                  <StatusBadge value={b.status} type="status" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
